<template>
    <div v-if="configData">
        <form-wrapper :validator="$v.configData" class="form-wrapper md-layout-item form">
            <div class="md-layout">
                <div class="md-layout-item md-size-20 md-medium-size-30 md-small-size-50 md-xsmall-size-100">
                    <md-radio :value="false" v-model="configData.isFortnightly">
                        {{ formLabels.occurenceType.weekly }}
                    </md-radio>
                    <md-radio :value="true" v-model="configData.isFortnightly">
                        {{ formLabels.occurenceType.fortnightly }}
                    </md-radio>
                </div>
                <div
                    class="md-layout-item md-size-20 md-medium-size-30 md-small-size-50 md-xsmall-size-100"
                    v-if="configData.isFortnightly"
                >
                    <md-datepicker
                        v-model="configData.fortnightlyStartDate"
                        :md-disabled-dates="disableNonMondays"
                        md-immediately
                        :md-debounce="10"
                    >
                        <label>{{ formLabels.fortnightlyStartDate }}</label>
                    </md-datepicker>
                    <span v-if="isFormSubmitted && configData.fortnightlyStartDate == null" class="error error-message">
                        {{ formLabels.fortnightlyStartDate }} is required.
                    </span>
                </div>
                <div class="md-layout-item md-size-100" v-if="weeklyData != null && weeklyData.length > 0">
                    <md-table class="custom-paginated-table">
                        <md-table-row slot="header">
                            <md-table-head class="label-column"></md-table-head>
                            <md-table-head class="day-header" v-for="col in dayColumns" :key="col">
                                {{ col }}
                            </md-table-head>
                        </md-table-row>
                        <md-table-row v-for="week in availableWeeks" :key="week.label">
                            <md-table-cell class="label-column" v-if="configData.isFortnightly">
                                {{ week.label }}
                            </md-table-cell>
                            <md-table-cell class="label-column" v-else></md-table-cell>
                            <md-table-cell v-for="day in week.days" :key="day.key">
                                <md-checkbox class="day-checkbox" v-model="day.checked"></md-checkbox>
                            </md-table-cell>
                        </md-table-row>
                    </md-table>
                </div>
            </div>
        </form-wrapper>

        <div class="form-actions action-section">
            <md-button class="md-primary pull-right" @click="handleSubmit">
                Save Roster
            </md-button>
        </div>

        <!-- Unavailable Days Section -->
        <form-wrapper :validator="$v.blockoutDays" class="unavailable-days-section md-layout">
            <div class="md-layout-item md-small-size-100 md-size-70">
                <div class="header-section">
                    <h5 class="section-title">Unavailable Days</h5>
                    <md-button
                        :disabled="onGoingUpdateOfBlockoutDay || disableActionButtons"
                        :class="onGoingUpdateOfBlockoutDay || disableActionButtons ? 'md-disabled' : ''"
                        class="md-button md-primary"
                        @click="addNewBlockoutDay"
                    >
                        <md-icon>add</md-icon>
                        Add Unavailable Day
                    </md-button>
                </div>
                <md-table v-if="hasBlockoutDays" class="custom-paginated-table blockout-day-table">
                    <md-table-row slot="header">
                        <md-table-head>Reason</md-table-head>
                        <md-table-head class="start-date">Start Date</md-table-head>
                        <md-table-head class="end-date">End Date</md-table-head>
                        <md-table-head>Attachments</md-table-head>
                        <md-table-head class="action-column">Actions</md-table-head>
                    </md-table-row>

                    <md-table-row
                        v-for="(blockoutDay, index) in blockoutDays"
                        :key="index"
                        class="row-data"
                        :class="{ 'selected-row': blockoutDay.inEditMode || blockoutDay.inAddMode }"
                    >
                        <md-table-cell class="reason-column">
                            <form-group name="reason" v-if="blockoutDay.inEditMode || blockoutDay.inAddMode">
                                <md-input v-model="blockoutDay.reason" maxlength="50" />
                            </form-group>
                            <p v-else>{{ blockoutDay.reason }}</p>
                        </md-table-cell>
                        <md-table-cell class="start-date">
                            <md-datepicker
                                v-if="blockoutDay.inEditMode || blockoutDay.inAddMode"
                                v-model="blockoutDay.startDate"
                                md-immediately
                                :md-debounce="100"
                                data-testid="blockout-startDate"
                                :md-model-type="String"
                            ></md-datepicker>
                            <p v-else>{{ transformDate(blockoutDay.startDate) }}</p>
                            <span
                                v-if="blockoutDay.isTouched && blockoutDay.startDate == null"
                                class="error error-message"
                            >
                                {{ formLabels.blockoutDays.startDate }} is required.
                            </span>
                            <span
                                v-if="
                                    blockoutDay.isTouched &&
                                        blockoutDay.startDate != null &&
                                        blockoutDay.endDate != null &&
                                        !$_isValidDateRange(blockoutDay.startDate, blockoutDay.endDate)
                                "
                                class="error error-message"
                            >
                                Invalid date range.
                            </span>
                        </md-table-cell>
                        <md-table-cell class="start-date">
                            <md-datepicker
                                v-if="blockoutDay.inEditMode || blockoutDay.inAddMode"
                                v-model="blockoutDay.endDate"
                                md-immediately
                                :md-debounce="100"
                                data-testid="blockout-endDate"
                                :md-model-type="String"
                            ></md-datepicker>
                            <p v-else>{{ transformDate(blockoutDay.endDate) }}</p>
                            <span
                                v-if="blockoutDay.isTouched && blockoutDay.endDate == null"
                                class="error error-message"
                            >
                                {{ formLabels.blockoutDays.endDate }} is required.
                            </span>
                        </md-table-cell>
                        <md-table-cell class="attachment-column">
                            <div
                                class="attachment-container"
                                v-if="hasDocumentAttachment(blockoutDay) || hasPhotoAttachment(blockoutDay)"
                            >
                                <blockout-days-document
                                    :blockout-day="blockoutDay"
                                    :has-document-attachment="hasDocumentAttachment"
                                />
                                <blockout-days-image
                                    :blockout-day="blockoutDay"
                                    :has-photo-attachment="hasPhotoAttachment"
                                />
                            </div>

                            <div class="attachment-container" v-if="blockoutDay.inAddMode || blockoutDay.inEditMode">
                                <blockout-days-attachment-group :attachment-list="documentList" />
                                <blockout-days-attachment-group :attachment-list="imageList" :file-type="'image'" />
                            </div>
                        </md-table-cell>
                        <md-table-cell class="action-column">
                            <blockout-days-button-group
                                :blockout-day="blockoutDay"
                                :index="index"
                                :team-member-roster-configuration-id="configData.teamMemberRosterConfigurationId"
                                :disable-action-buttons="disableActionButtons"
                                :image-list="imageList"
                                :document-list="documentList"
                                :is-not-valid-date="isNotValidDate"
                                @update="editBlockoutDay"
                                @undo-changes="undoChangesOnBlockoutDay"
                                @remove="removeBlockoutDay"
                                @saveChanges="handleSaveChanges"
                                @delete="handleDeleteBlockoutDay"
                                @imageListUpdate="handleImageListUpdate"
                                @documentListUpdate="handleDocumentListUpdate"
                                @blockoutDayUpdated="handleConfigurationUpdated"
                                @showErrorMessage="showErrorMessage"
                            />
                        </md-table-cell>
                    </md-table-row>
                </md-table>
                <div class="text-center" v-if="!hasBlockoutDays">
                    <p class="no-result-message">No unavailable days.</p>
                </div>
            </div>
        </form-wrapper>
    </div>
</template>

<script>
import moment from 'moment';
import { handleRequests, compareProperties } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import { requiredIf, required } from 'vuelidate/lib/validators';
import { ReverseBitwise, GetBitwiseFlagsValue } from '@/utils/ApplyRatesToUtils';
import { AVAILABLE_DAYS_FLAGS, DATE_TYPES, DAYS_OF_THE_WEEK, DAYS_OF_THE_WEEK_LIST } from '@/utils/constants';
import { TEAM_MEMBER_ROSTER_BLOCKOUT_DAY } from '@/utils/defaults';
import BlockoutDaysButtonGroup from './BlockoutDaysButtonGroup';
import BlockoutDaysAttachmentGroup from './BlockoutDaysAttachmentGroup';
import BlockoutDaysImage from './BlockoutDaysImage';
import BlockoutDaysDocument from './BlockoutDaysDocument';

export default {
    name: 'MemberRosterConfiguration',
    mixins: [GeneralMixin],
    props: {
        publicUserId: {
            type: String,
            required: true
        },
        rosterConfigurationData: {
            type: Object,
            default: () => {},
            required: true
        }
    },
    components: {
        BlockoutDaysButtonGroup,
        BlockoutDaysAttachmentGroup,
        BlockoutDaysImage,
        BlockoutDaysDocument
    },
    data() {
        return {
            configData: null,
            dayColumns: DAYS_OF_THE_WEEK_LIST,
            weeklyData: [
                {
                    label: 'Week 1', // Normal Week
                    isVisible: true,
                    isFortnightly: false,
                    days: [
                        { key: 1, label: DAYS_OF_THE_WEEK.Monday, checked: false },
                        { key: 2, label: DAYS_OF_THE_WEEK.Tuesday, checked: false },
                        { key: 4, label: DAYS_OF_THE_WEEK.Wednesday, checked: false },
                        { key: 8, label: DAYS_OF_THE_WEEK.Thursday, checked: false },
                        { key: 16, label: DAYS_OF_THE_WEEK.Friday, checked: false },
                        { key: 32, label: DAYS_OF_THE_WEEK.Saturday, checked: false },
                        { key: 64, label: DAYS_OF_THE_WEEK.Sunday, checked: false }
                    ]
                },
                {
                    label: 'Week 2', // Fortnightly Week
                    isVisible: false,
                    isFortnightly: true,
                    days: [
                        { key: 128, label: DAYS_OF_THE_WEEK.Monday, checked: false },
                        { key: 256, label: DAYS_OF_THE_WEEK.Tuesday, checked: false },
                        { key: 512, label: DAYS_OF_THE_WEEK.Wednesday, checked: false },
                        { key: 1024, label: DAYS_OF_THE_WEEK.Thursday, checked: false },
                        { key: 2048, label: DAYS_OF_THE_WEEK.Friday, checked: false },
                        { key: 4096, label: DAYS_OF_THE_WEEK.Saturday, checked: false },
                        { key: 8192, label: DAYS_OF_THE_WEEK.Sunday, checked: false }
                    ]
                }
            ],
            isFormSubmitted: false,
            formLabels: {
                fortnightlyStartDate: 'Roster Cycle Start Date',
                occurenceType: {
                    weekly: 'Weekly',
                    fortnightly: 'Fortnightly'
                },
                blockoutDays: {
                    startDate: 'Start Date',
                    endDate: 'End Date'
                }
            },
            origAvailableDaysFlagsArray: [],
            disableNonMondays: (date) => {
                const day = moment(date).day();
                return day !== 1; // Disable all days except Monday
            },
            origBlockoutDay: null,
            images: [],
            imageList: [],
            imageUrls: [],
            documents: [],
            documentList: [],
            documentUrls: []
        };
    },
    validations: {
        configData: {
            fortnightlyStartDate: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.configData.isFortnightly;
                    })
            }
        },
        blockoutDays: {
            $each: {
                startDate: {
                    required
                },
                endDate: {
                    required
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        availableWeeks() {
            return this.weeklyData.filter((week) => week.isVisible);
        },
        hasBlockoutDays() {
            return this.configData.blockoutDays?.length > 0;
        },
        blockoutDays() {
            return this.configData.blockoutDays ?? [];
        },
        onGoingUpdateOfBlockoutDay() {
            return this.configData.blockoutDays.some((blockoutDay) => blockoutDay.inEditMode);
        },
        disableActionButtons() {
            return this.onGoingUpdateOfBlockoutDay || this.onGoingAddBlockoutDay;
        },
        onGoingAddBlockoutDay() {
            return this.configData.blockoutDays.some((blockoutDay) => blockoutDay.inAddMode);
        }
    },
    async mounted() {
        this.syncData();
    },
    watch: {
        'configData.isFortnightly': function(value) {
            this.configData.isFortnightly = value;

            const fortnightlyWeek = this.weeklyData.find((week) => week.isFortnightly);
            if (fortnightlyWeek) {
                fortnightlyWeek.isVisible = value;
            }

            if (!value) {
                this.configData.fortnightlyStartDate = null;
                fortnightlyWeek.days.forEach((day) => {
                    day.checked = false;
                });

                this.isFormSubmitted = false;
            }
        },
        rosterConfigurationData() {
            this.syncData();
        }
    },
    methods: {
        syncData() {
            this.configData = _.cloneDeep(this.rosterConfigurationData);
            this.syncWeeklyData();
        },
        syncWeeklyData() {
            this.origAvailableDaysFlagsArray = ReverseBitwise(this.configData.availableDayFlags, AVAILABLE_DAYS_FLAGS);

            this.weeklyData.forEach((week) => {
                week.days.forEach((day) => {
                    day.checked = this.origAvailableDaysFlagsArray.includes(day.key);
                });
            });
        },
        isNotValidDate(startDate, endDate) {
            return !startDate || !endDate || !this.$_isValidDateRange(startDate, endDate);
        },
        async handleSubmit() {
            this.$v.$touch();
            this.isFormSubmitted = true;
            this.configData.blockoutDays
                .filter((x) => this.isNotValidDate(x.startDate, x.endDate))
                .forEach((blockoutDay) => {
                    blockoutDay.isTouched = true;
                });

            if (
                this.$v.configData.$invalid ||
                this.$v.blockoutDays.$invalid ||
                this.configData.blockoutDays.some((x) => this.isNotValidDate(x.startDate, x.endDate))
            ) {
                return;
            }

            if (this.configData.blockoutDays.some((day) => day.inAddMode || day.inEditMode)) {
                this.$notify({
                    message:
                        "Some 'Unavailable Days' rows are still being edited. Please save or cancel them before proceeding.",
                    type: 'warning'
                });
                return;
            }

            this.$_handleLoaderState(true);
            this.configData.availableDayFlags = GetBitwiseFlagsValue(this.weeklyData.flatMap((week) => week.days));
            this.configData.fortnightlyStartDate = this.configData.isFortnightly
                ? moment(this.configData.fortnightlyStartDate).format(DATE_TYPES.internationalDate)
                : null;

            const data = { ...this.configData, publicUserId: this.publicUserId };

            const endpoint = `/api/team-members/roster-configuration`;
            const payload = {
                method: 'post',
                data
            };
            try {
                await handleRequests(endpoint, payload);
                this.$notify({
                    message: 'Roster configuration successfully saved',
                    type: 'success'
                });

                this.isFormSubmitted = false;
                this.handleConfigurationUpdated();
            } catch (error) {
                const message = 'Error in saving roster configuration';
                this.showErrorMessage(this, message, error);

                this.$_handleLoaderState(false);
                this.isFormSubmitted = false;
            }

            this.origBlockoutDay = null;
        },
        hasChangesOnProperties() {
            return !compareProperties(this.configData, this.rosterConfigurationData, [
                'isFortnightly',
                'fortnightlyStartDate',
                'blockoutDays'
            ]);
        },
        hasChangesOnAvailableDays() {
            const currentAvailableDayFlagValue = GetBitwiseFlagsValue(this.weeklyData.flatMap((week) => week.days));
            const currentAvailableDaysFlagsArray = ReverseBitwise(currentAvailableDayFlagValue, AVAILABLE_DAYS_FLAGS);

            const combinedFlagArray = currentAvailableDaysFlagsArray.concat(this.origAvailableDaysFlagsArray);

            // Having unique values means that a checkbox has been either ticked or unticked
            const uniqueFlagValues = combinedFlagArray.filter(
                (item) => combinedFlagArray.indexOf(item) === combinedFlagArray.lastIndexOf(item)
            );

            return uniqueFlagValues.length > 0;
        },
        hasChanges() {
            return this.hasChangesOnProperties() || this.hasChangesOnAvailableDays();
        },
        addNewBlockoutDay() {
            const newBlockout = Object.assign({}, TEAM_MEMBER_ROSTER_BLOCKOUT_DAY);
            this.configData.blockoutDays.unshift(newBlockout);
        },
        handleImageListUpdate(imageList) {
            this.imageList = imageList;
        },
        handleDocumentListUpdate(documentList) {
            this.documentList = documentList;
        },
        handleConfigurationUpdated() {
            this.$emit('rosterConfigurationUpdated');
        },
        removeBlockoutDay(index) {
            this.configData.blockoutDays.splice(index, 1);
            this.imageList = [];
            this.documentList = [];
        },
        async handleDeleteBlockoutDay(event) {
            if (event) {
                this.handleConfigurationUpdated();
            }
        },
        showErrorMessage(el, message, error) {
            if (error && error.data && error.status === 400) {
                if (Array.isArray(error.data) && error.data.length > 0) {
                    error.data.forEach((e) => {
                        el.$notify({
                            message: e,
                            type: 'danger'
                        });
                    });
                } else if (typeof error.data === 'string') {
                    el.$notify({
                        message: error.data,
                        type: 'danger'
                    });
                } else {
                    el.$notify({
                        message,
                        type: 'danger'
                    });
                }
            } else {
                el.$notify({
                    message,
                    type: 'danger'
                });
            }
        },
        editBlockoutDay(blockoutDay) {
            this.origBlockoutDay = _.cloneDeep(blockoutDay);
            blockoutDay.inEditMode = true;
        },
        undoChangesOnBlockoutDay(blockoutDay) {
            if (!blockoutDay || !blockoutDay.inEditMode || !this.origBlockoutDay)
                return;

            // Revert to original values
            blockoutDay.startDate = this.origBlockoutDay.startDate;
            blockoutDay.endDate = this.origBlockoutDay.endDate;
            blockoutDay.inEditMode = false;
            blockoutDay.reason = this.origBlockoutDay.reason;
            this.origBlockoutDay = null;

            this.imageList = [];
            this.documentList = [];
        },
        async handleSaveChanges(blockoutDay) {
            this.origBlockoutDay = null;
        },
        transformDate(date) {
            return moment(date).format('MMM D, YYYY');
        },
        canDisplayDocumentAttachment(blockoutDay) {
            return this.hasDocumentAttachment(blockoutDay) && !this.hasPhotoAttachment(blockoutDay);
        },
        canDisplayPhotoAttachment(blockoutDay) {
            return this.hasPhotoAttachment(blockoutDay) && !this.hasDocumentAttachment(blockoutDay);
        },
        hasDocumentAttachment(blockoutDay) {
            return (
                blockoutDay.attachments &&
                blockoutDay.attachments.length > 0 &&
                !!blockoutDay.attachments.find((att) => att.documentUrls?.length > 0)
            );
        },
        hasPhotoAttachment(blockoutDay) {
            return (
                blockoutDay.attachments &&
                blockoutDay.attachments.length > 0 &&
                !!blockoutDay.attachments.find((att) => att.photoUrls?.length > 0)
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.form {
    margin: 30px 0;
}
.day-checkbox {
    display: flex;
    justify-content: center;
    align-items: center;
}
.day-header {
    text-align: center;
}
.label-column {
    width: 5%;
}
.error-message {
    margin-left: 35px;
}

.action-section {
    width: 100% !important;
    height: 50px;
    padding: 0 15px;
}

.unavailable-days-section {
    margin-top: 20px;

    .header-section {
        width: 100%;
        height: 48px;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .row-data {
        height: 70px !important;
    }

    p {
        margin: 0;
    }

    .selected-row {
        background-color: #f5f5f5 !important;
        height: 100px !important;
    }

    .start-date,
    .end-date {
        width: 20% !important;
        max-width: 10px;
    }

    .reason-column {
        width: 20% !important;
        max-width: 100px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .attachment-column {
        width: 20% !important;
        max-width: 100px;
        text-align: start;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        font-size: 13px;

        .md-button {
            width: 100% !important;
            height: 100% !important;
            font-weight: 500 !important;
        }
    }

    .action-column {
        width: 20% !important;
        text-align: center;
    }

    .md-table-cell {
        padding: 8px;
    }

    .section-title {
        margin: 0;
        font-weight: 500;
    }

    .no-result-message {
        margin: 16px 0;
        font-size: medium;
        font-style: italic;
    }

    .attachment-container {
        display: flex;
        flex-direction: column;
        gap: 5px;

        a {
            cursor: pointer;
            color: black;
            font-size: 13px;
        }

        .document-list {
            list-style: none;
            padding: 0;
            margin: 0;

            display: flex;
            align-items: center;
            gap: 3px;
            flex-wrap: wrap;
        }

        p {
            margin: 0;
            padding: 0;
        }
    }

    .blockout-day-table .md-table-row:nth-child(n + 3) > td {
        border-top: 0 !important;
    }
}
</style>
